import deTranslations from '../locales/de.json';
import enTranslations from '../locales/en.json';

/**
 * Translation for the ops dashboard.
 *
 * Hand-rolled and dependency-free, the same shape as `apps/portal/src/i18n` — `{{token}}`
 * interpolation, a dotted key path, no pluralisation formatter — so copy is portable between the
 * surfaces and a reviewer sees one convention. What differs from the portal is the RESOLUTION
 * ORDER: this tool serves no tokenised links, so there is no `?locale=` to honour and no shop
 * locale to adopt. The browser decides on the first visit, the choice is persisted, and the
 * header switcher overrides both — see {@link resolveInitialLocale}.
 *
 * **Infrastructure enum tokens are deliberately NOT translated.** Job states (`waiting`,
 * `failed`), queue names and alert severities render as the raw value in both locales, because an
 * operator mid-incident matches them against Sentry, Redis and the queue itself. Those are
 * identifiers, and identifiers stay identifiers.
 *
 * **The support desk's enums ARE translated** — statuses, categories, priorities, tiers, history
 * event types and author types, through `lib/support-enum-labels.ts`. They are not infrastructure:
 * an agent picks one from a dropdown to describe a conversation, and a German agent choosing
 * between `TAX_COMPLIANCE` and `DATA_EXPORT` is reading a database schema, not a form. The raw
 * token stays reachable where the cross-check actually happens — the queue's status badge and each
 * history row carry it in a `title`.
 *
 * Strings that come off the API — `health.issues`, a validation message from the backend — arrive
 * already-worded and are rendered as received. Translating those belongs to the API's own
 * `i18nServer`, not here.
 */

/** Locales this app ships. */
export const SUPPORTED_LOCALES = ['de', 'en'] as const;

/** One of {@link SUPPORTED_LOCALES}. */
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Fallback when nothing else resolves.
 *
 * English, unlike the portal's German: this tool's copy has always been English, its queue and
 * job vocabulary is English either way, and an operator whose browser asks for neither German nor
 * English is the case where the lingua franca beats a guess. A German browser still gets German —
 * that is `normaliseLocale`'s job, not this constant's.
 */
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Where the choice is persisted.
 *
 * Namespaced like the other keys this origin owns (`bullmq_admin_token`, …) but with the app's
 * own prefix rather than the portal's, so the two never collide if they are ever served from one
 * host. Cleared by nothing — a language preference must survive a sign-out.
 */
const LOCALE_STORAGE_KEY = 'easy-invoices-ops-locale';

const translations: Record<SupportedLocale, unknown> = {
  en: enTranslations,
  de: deTranslations,
};

/** Values a caller may interpolate into a string. */
export type TranslationVars = Record<string, string | number>;

/** Whether a string is a locale this app ships. */
export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Normalises anything locale-ish onto a supported locale.
 *
 * `de-DE`, `de_AT` and `Deutsch` all mean German; `en-GB` and `en_US` mean English. Everything
 * else becomes the default rather than throwing — a wrong language is recoverable from the header
 * switcher, a crash on an unexpected `navigator.language` is not.
 */
export function normaliseLocale(
  value: string | null | undefined,
): SupportedLocale {
  if (!value) {
    return DEFAULT_LOCALE;
  }
  const head = value.toLowerCase().split(/[-_]/)[0];
  if (head === 'de' || head === 'deutsch') {
    return 'de';
  }
  if (head === 'en' || head === 'english') {
    return 'en';
  }
  return DEFAULT_LOCALE;
}

/** Walks a dotted path through the catalogue. */
function lookup(locale: SupportedLocale, key: string): string | undefined {
  let node: unknown = translations[locale];
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) {
      return undefined;
    }
    node = Object.entries(node).find(([name]) => name === part)?.[1];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * Substitutes `{{name}}` placeholders.
 *
 * A missing variable leaves the placeholder in place rather than throwing or emptying it, which is
 * what the portal and `i18nServer.t` both do: visibly wrong copy gets reported, silently missing
 * copy does not.
 */
function interpolate(
  template: string,
  vars: TranslationVars | undefined,
): string {
  if (!vars) {
    return template;
  }
  return template.replace(/\{\{(\w+)\}\}/g, (placeholder, name: string) => {
    const value = vars[name];
    return value === undefined ? placeholder : String(value);
  });
}

/**
 * Translates a key.
 *
 * Falls back to the default locale, then to the key itself. Returning the key is deliberate: it
 * makes a missing string obvious on screen and in a screenshot, rather than rendering an empty
 * element that reads as a layout bug.
 */
export function translate(
  locale: SupportedLocale,
  key: string,
  vars?: TranslationVars,
): string {
  const direct = lookup(locale, key);
  if (direct !== undefined) {
    return interpolate(direct, vars);
  }
  const fallback = lookup(DEFAULT_LOCALE, key);
  return fallback === undefined ? key : interpolate(fallback, vars);
}

/**
 * The `_one` / `_other` variant of a key.
 *
 * Explicit sibling keys rather than a formatter, matching the portal: the absence of a plural
 * form is then visible in the catalogue and caught by the parity spec, instead of hidden inside a
 * library. German and English share the `n === 1` rule, which is the only rule these two
 * catalogues need.
 */
export function pluralKey(key: string, count: number): string {
  return `${key}_${count === 1 ? 'one' : 'other'}`;
}

/** A literal run of copy, or a `{{name}}` hole a caller fills with a node. */
export type TemplatePart =
  | { readonly text: string }
  | { readonly name: string };

/**
 * Splits a template into literal runs and `{{name}}` holes.
 *
 * This is what lets one catalogue string carry inline markup without the markup being IN the
 * catalogue: the caller hands `tNode` a `<code>` element per placeholder and the sentence stays a
 * single translatable unit. Chopping a sentence into `…before` / `…after` keys instead would fix
 * English word order into the German copy, which is the actual reason this exists.
 */
export function splitTemplate(template: string): TemplatePart[] {
  const parts: TemplatePart[] = [];
  // `exec` in a loop rather than `matchAll`: the iterator's match type declares `index` as
  // optional, so every slice below would need a non-null assertion this way round.
  const pattern = /\{\{(\w+)\}\}/g;
  let cursor = 0;
  let match = pattern.exec(template);
  while (match !== null) {
    if (match.index > cursor) {
      parts.push({ text: template.slice(cursor, match.index) });
    }
    parts.push({ name: match[1] });
    cursor = match.index + match[0].length;
    match = pattern.exec(template);
  }
  if (cursor < template.length) {
    parts.push({ text: template.slice(cursor) });
  }
  return parts;
}

/** Reads the persisted locale preference, tolerating a storage-less browser. */
export function readStoredLocale(): SupportedLocale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : null;
  } catch {
    // Private mode, or storage disabled. Not a failure worth surfacing.
    return null;
  }
}

/** Persists the locale preference, tolerating a storage-less browser. */
export function storeLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // As above.
  }
}

/**
 * Resolves the locale to start in.
 *
 * Order: the stored preference, then the browser language, then {@link DEFAULT_LOCALE}. Stored
 * FIRST is the whole point — the browser is only a first guess, and once an operator has picked
 * from the header switcher that pick outranks whatever Chrome is configured with. Nothing here
 * ever writes: persisting is the switcher's job, so a first visit that merely detects German does
 * not lock German in.
 *
 * @param browserLanguage `navigator.language`, passed in so this is testable without a DOM.
 */
export function resolveInitialLocale(
  browserLanguage?: string | null,
): SupportedLocale {
  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }
  if (browserLanguage) {
    return normaliseLocale(browserLanguage);
  }
  return DEFAULT_LOCALE;
}
